import React from 'react';
import { ArrowTopRightOnSquareIcon, CheckBadgeIcon, DocumentMagnifyingGlassIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { IntelligenceAnalysisReport } from '../../../functions/src/common/intelligenceAnalysis';
import { intelligenceAnalysisStyles as styles } from '../../common/intelligenceAnalysisStyles';
import { formatLocalDateTime } from '../../utils/localDateTime';
import './intelligenceAnalysis.css';

const claimLabels = { verified: 'Verified', partial: 'Partially verified', disputed: 'Disputed', unverified: 'Unverified' };

const SectionHeading: React.FC<{ number: string; title: string; detail?: string }> = ({ number, title, detail }) =>
    <div className="intel-section-heading"><span className="intel-section-number" aria-hidden="true">{number}</span>
        <div><h3>{title}</h3>{detail && <p className={styles.muted}>{detail}</p>}</div></div>;

const ScoreBar: React.FC<{ label: string; score: number }> = ({ label, score }) =>
    <div className="intel-score-bar" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={score}>
        <span style={{ width: score + '%' }} />
    </div>;

const IntelligenceAnalysisDisplay: React.FC<{ result: IntelligenceAnalysisReport }> = ({ result }) => {
    const counts = { verified: 0, partial: 0, disputed: 0, unverified: 0 };
    for (const claim of result.claims)
        counts[claim.status]++;
    const sources = new Map(result.sources.map(source => [source.id, source]));
    return <article className="intelligence-analysis">
        <header className="intel-hero">
            <div className="intel-hero-meta"><span className="intel-eyebrow"><DocumentMagnifyingGlassIcon aria-hidden="true" />Intelligence assessment</span>
                <span className="intel-date">{formatLocalDateTime(result.generatedAt)}</span></div>
            <h2>{result.title}</h2>
            <p className="intel-hero-caption">Client intelligence brief <span aria-hidden="true">/</span> AI-generated assessment</p>
            <dl className="intel-metrics">
                <div className="intel-metric"><dt>Verification</dt><dd>{result.verificationScore}<span>/100</span></dd>
                    <ScoreBar label="Verification" score={result.verificationScore} /><p>Evidence supporting the submitted claims</p></div>
                <div className="intel-metric"><dt>Confidence</dt><dd>{result.confidenceScore}%</dd>
                    <ScoreBar label="Confidence" score={result.confidenceScore} /><p>Strength and coverage of available evidence</p></div>
                <div className="intel-metric"><dt>Client impact</dt><dd><span className="intel-impact" data-impact={result.impact}>{result.impact}</span></dd>
                    <p>{result.claims.length} claims assessed · {result.sources.length} retrieved sources</p></div>
            </dl>
        </header>

        <section className="intel-summary">
            <CheckBadgeIcon aria-hidden="true" /><div><h3>Executive summary</h3><p className={styles.prose}>{result.summary}</p></div>
        </section>

        <section className="intel-panel">
            <SectionHeading number="01" title="Context and business impact" detail="What this means for your operations" />
            <p className={styles.prose}>{result.implications}</p>
        </section>

        <section className="intel-panel">
            <SectionHeading number="02" title="Evidence assessment" detail="How the reporting holds up" />
            <div className="intel-evidence-grid">
                <div><h4>Credibility</h4><p className={styles.prose}>{result.credibility}</p></div>
                <div><h4>Corroboration and contradictions</h4><p className={styles.prose}>{result.corroboration}</p></div>
            </div>
        </section>

        <section className="intel-panel">
            <SectionHeading number="03" title="Recommended actions" detail="Priorities, reasons, and signals to watch" />
            <ol className="intel-actions">{result.actions.map((action, index) => <li key={index} className="intel-action" data-priority={action.priority}>
                <div className="intel-action-meta"><span className="intel-action-number">{String(index + 1).padStart(2, '0')}</span>
                    <span className="intel-priority">{action.priority} priority</span></div>
                <h4>{action.action}</h4><p className={styles.prose}>{action.rationale}</p>
                <p className="intel-trigger"><strong>Watch for</strong>{action.trigger}</p>
            </li>)}</ol>
        </section>

        <section className="intel-panel">
            <SectionHeading number="04" title="Claim verification" detail="Each claim checked against retrieved evidence" />
            <div className="intel-claim-counts">{Object.entries(counts).map(([status, count]) =>
                <span key={status} className="intel-status" data-status={status}>{count} {claimLabels[status as keyof typeof claimLabels].toLowerCase()}</span>)}</div>
            <div className="intel-table-wrap"><table className="intel-claims">
                <caption className={styles.muted}>Admiralty ratings combine source reliability (A–F) with information credibility (1–6). F6 means unassessed.</caption>
                <thead><tr><th scope="col">Claim</th><th scope="col">Assessment</th><th scope="col">Evidence</th></tr></thead>
                <tbody>{result.claims.map((claim, index) => <tr key={index}>
                    <td data-label="Claim" className="intel-claim-text">{claim.claim}</td>
                    <td data-label="Assessment"><div className="intel-claim-rating"><span className="intel-status" data-status={claim.status}>{claimLabels[claim.status]}</span>
                        <span className="intel-rating">{claim.rating}</span></div><p className={styles.prose}>{claim.reason}</p></td>
                    <td data-label="Evidence">{claim.sourceIds.length ? <div className="intel-citations">{claim.sourceIds.map(id => {
                        const source = sources.get(id);
                        return source ? <a key={id} href={source.url} target="_blank" rel="noopener noreferrer" title={source.title}>{id}</a> : null;
                    })}</div> : <span className={styles.muted}>No retrieved evidence</span>}</td>
                </tr>)}</tbody>
            </table></div>
        </section>

        <section className="intel-panel">
            <SectionHeading number="05" title="Scores and evidence gaps" detail="The basis and limits of this assessment" />
            <div className="intel-confidence-grid"><dl className="intel-factors">{Object.entries(result.confidenceFactors).map(([factor, score]) =>
                <div key={factor}><div className="intel-factor-label"><dt>{factor}</dt><dd>{score}<span>/100</span></dd></div>
                    <ScoreBar label={factor + ' confidence factor'} score={score} /></div>)}</dl>
                <div className="intel-gaps"><h4><ExclamationTriangleIcon aria-hidden="true" />Evidence gaps</h4>
                    {result.dataGaps.length ? <ul>{result.dataGaps.map((gap, index) => <li key={index}>{gap}</li>)}</ul>
                        : <p className={styles.prose}>No additional gaps were listed in this assessment.</p>}</div></div>
            <p className="intel-method">{result.scoreMethod}</p>
        </section>

        <section className="intel-panel">
            <SectionHeading number="06" title="Sources" detail="Trace the assessment back to its evidence" />
            {result.sources.length ? <ol className="intel-sources">{result.sources.map(source => <li key={source.id}>
                <span className="intel-source-id">{source.id}</span><div><a href={source.url} target="_blank" rel="noopener noreferrer" className={styles.link}>
                    {source.title}<ArrowTopRightOnSquareIcon aria-hidden="true" /></a><p>{source.url}</p></div>
            </li>)}</ol> : <p className={styles.muted}>No independent sources were retrieved. Claims remain unverified.</p>}
            <div className="intel-submitted"><h4>Submitted intelligence</h4>
                <p className={styles.muted}>Submitted material: {result.submittedSources.join(', ') || 'Pasted intelligence text'}</p>
                {result.questions && <p className={styles.prose}><strong>Client questions: </strong>{result.questions}</p>}
                {result.context && <p className={styles.prose}><strong>Client context: </strong>{result.context}</p>}
            </div>
        </section>
        <p className="intel-disclaimer">This automated assessment reflects available evidence at generation time. It has not received consultant validation. Review material decisions against primary sources.</p>
    </article>;
};

export default IntelligenceAnalysisDisplay;
